import React from 'react';
import { FiCheckCircle, FiMapPin, FiPhone, FiPlayCircle, FiThumbsUp, FiXCircle } from 'react-icons/fi';

// Journey log of a trip (trip.journeyLog) as a bar of dots in time order. Hovering a dot shows the
// action and its time; clicking it opens the details (who, when, note/reason, driver location) and
// the map focuses on where the driver was.

export const JOURNEY_TYPES = {
    offer_accepted: { label: 'قبول العرض', color: '#2563eb', Icon: FiThumbsUp },
    call: { label: 'اتصال', color: '#8b5cf6', Icon: FiPhone },
    trip_started: { label: 'بدء الرحلة', color: '#f59e0b', Icon: FiPlayCircle },
    trip_completed: { label: 'انتهاء الرحلة', color: '#16a34a', Icon: FiCheckCircle },
    trip_cancelled: { label: 'إلغاء الرحلة', color: '#dc2626', Icon: FiXCircle },
};

const ACTORS = { user: 'المستخدم', shopOwner: 'صاحب الورشة', driver: 'السائق', admin: 'الإدارة', system: 'النظام' };
const CHANNELS = { phone: 'اتصال هاتفي', whatsapp: 'واتساب' };

const formatTime = (value) => (value ? new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—');
const formatDateTime = (value) => (value ? `${new Date(value).toLocaleDateString('en-GB')} - ${formatTime(value)}` : '—');

const describe = (event) => {
    const actor = ACTORS[event.actor] || event.actor;
    if (event.type === 'call') {
        const target = event.actor === 'driver' ? 'المستخدم' : 'السائق';
        return `${actor} اتصل بـ${target} (${CHANNELS[event.meta?.channel] || CHANNELS.phone})`;
    }
    if (event.type === 'offer_accepted') return `${actor} قبل عرض السائق`;
    if (event.type === 'trip_started') return `${actor} بدأ الرحلة`;
    if (event.type === 'trip_completed') return `${actor} أنهى الرحلة`;
    if (event.type === 'trip_cancelled') return `${actor} ألغى الرحلة`;
    return actor;
};

const TripJourneyTimeline = ({ events = [], selectedIndex, onSelect }) => {
    if (!events.length) {
        return (
            <div className="journey-log">
                <h3>سجل الرحلة</h3>
                <p className="journey-empty">لا توجد سجلات لهذه الرحلة</p>
            </div>
        );
    }

    const selected = selectedIndex !== null && selectedIndex !== undefined ? events[selectedIndex] : null;
    const selectedInfo = selected ? (JOURNEY_TYPES[selected.type] || { label: selected.type, color: '#64748b' }) : null;
    const loc = selected?.driverLocation;
    const hasLoc = Number.isFinite(Number(loc?.latitude)) && Number.isFinite(Number(loc?.longitude));

    return (
        <div className="journey-log">
            <h3>سجل الرحلة</h3>
            <div className="journey-bar">
                <div className="journey-line" />
                {events.map((event, index) => {
                    const info = JOURNEY_TYPES[event.type] || { label: event.type, color: '#64748b' };
                    return (
                        <button
                            type="button"
                            key={`${index}-${event.at}`}
                            className={`journey-dot ${selectedIndex === index ? 'selected' : ''}`}
                            style={{ '--dot-color': info.color }}
                            onClick={() => onSelect(selectedIndex === index ? null : index)}
                            aria-label={`${info.label} ${formatTime(event.at)}`}
                        >
                            <span className="journey-tooltip">
                                <strong>{info.label}</strong>
                                <span>{formatTime(event.at)}</span>
                            </span>
                        </button>
                    );
                })}
            </div>
            <div className="journey-legend">
                {Object.entries(JOURNEY_TYPES)
                    .filter(([type]) => events.some((e) => e.type === type))
                    .map(([type, info]) => (
                        <span key={type}><i style={{ background: info.color }} />{info.label}</span>
                    ))}
            </div>

            {selected ? (
                <div className="journey-details" style={{ borderColor: selectedInfo.color }}>
                    <div className="journey-details__title" style={{ color: selectedInfo.color }}>
                        <selectedInfo.Icon /> {selectedInfo.label}
                    </div>
                    <div className="journey-details__row"><span>الإجراء</span><span>{describe(selected)}</span></div>
                    <div className="journey-details__row"><span>الوقت</span><span dir="ltr">{formatDateTime(selected.at)}</span></div>
                    {selected.type === 'offer_accepted' && selected.meta?.price ? (
                        <div className="journey-details__row"><span>قيمة العرض</span><span>{selected.meta.price} د.ل</span></div>
                    ) : null}
                    {selected.note ? (
                        <div className="journey-details__row"><span>{selected.type === 'trip_cancelled' ? 'سبب الإلغاء' : 'ملاحظة'}</span><span>{selected.note}</span></div>
                    ) : null}
                    <div className="journey-details__row">
                        <span>موقع السائق</span>
                        {hasLoc ? (
                            <a
                                href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`}
                                target="_blank"
                                rel="noreferrer"
                                dir="ltr"
                            >
                                <FiMapPin /> {Number(loc.latitude).toFixed(5)}, {Number(loc.longitude).toFixed(5)}
                            </a>
                        ) : <span>غير متوفر</span>}
                    </div>
                    {hasLoc && loc.updatedAt ? (
                        <div className="journey-details__row"><span>آخر تحديث للموقع</span><span dir="ltr">{formatDateTime(loc.updatedAt)}</span></div>
                    ) : null}
                </div>
            ) : (
                <p className="journey-hint">اضغط على أي نقطة لعرض تفاصيل الإجراء وموقع السائق وقتها</p>
            )}
        </div>
    );
};

export default TripJourneyTimeline;
